import React, { useEffect } from 'react';
import { colors } from '../styles/colors';

const PrivacyPolicyPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div 
      className="min-h-screen pt-24 pb-16 px-4 sm:px-6"
      style={{
        background: `linear-gradient(135deg, ${colors.black} 0%, ${colors.graphite} 100%)`,
      }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Page Heading */}
        <h1 
          className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2 text-center"
          style={{ color: colors.white }}
        >
          Privacy Policy
        </h1>
        
        {/* Publication Date */}
        <p 
          className="text-sm text-center mb-8"
          style={{ color: colors.white, opacity: 0.6 }}
        >
          Last updated: 27/11/2025
        </p>

        {/* Download Button */}
        <div className="flex justify-center mb-12 sm:mb-16">
          <a
            href="/privacy-policy.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-all duration-300 hover:scale-105"
            style={{
              backgroundColor: colors.teal,
              color: colors.white,
            }}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round" 
              strokeLinejoin="round"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            Download PDF
          </a>
        </div>

        {/* Document Content */}
        <div 
          className="prose prose-invert max-w-none"
          style={{ 
            color: colors.white,
            fontSize: '18px',
            lineHeight: '1.7'
          }}
        >
          <p style={{ marginBottom: '32px' }}>
            This Privacy Policy ("Policy") describes how Arizore LTD ("Arizore", "we", "us", or "our") collects, uses, processes, stores, discloses, and protects Personal Information when you access our website, submit information through online forms, participate in our onboarding processes, or access your user portal. This Policy applies to all visitors, applicants, registered users, and any individual interacting with Arizore services (collectively, "Users").
          </p>

          <p style={{ marginBottom: '32px' }}>
            By accessing or using our website or services, you acknowledge that you have read and understood this Policy. If you do not agree with its terms, you should discontinue use immediately.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            1. Who We Are
          </h2>
          <p style={{ marginBottom: '24px' }}>
            Arizore LTD is a corporation organized under the laws of Nevis. We operate a structured, real-world-asset ("RWA") reserve framework that supports mining projects, bullion providers, and other verified commercial entities. Our services include application intake, due-diligence review, verified resource assessment pathways, and access to a secure user portal for onboarding.
          </p>
          <p style={{ marginBottom: '12px' }}>
            Registered Address: The Provident House, Central Government Road, Charlestown, Nevis, W.I.
          </p>
          <p style={{ marginBottom: '24px' }}>
            Legal Contact: admin@azreserves.com
          </p>
          <p style={{ marginBottom: '24px' }}>
            Arizore acts as the Data Controller for the Personal Information described in this Policy, except where we act as a processor on behalf of verified partners.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            2. Information We Collect
          </h2>
          <p style={{ marginBottom: '24px' }}>
            We collect information that identifies, relates to, describes, is reasonably capable of being associated with, or could reasonably be linked, directly or indirectly, with a User ("Personal Information"). This includes:
          </p>

          <h3 style={{ fontSize: '22px', fontWeight: '600', marginTop: '24px', marginBottom: '12px', color: colors.white }}>
            2.1 Information You Provide Directly
          </h3>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Identity Information: first and last name</li>
            <li style={{ marginBottom: '8px' }}>Contact Information: email address, phone number</li>
            <li style={{ marginBottom: '8px' }}>Application Materials: documents, certificates, geological reports, business records, or other information submitted through the user portal</li>
            <li style={{ marginBottom: '8px' }}>Communication Records: messages sent through forms, email communications, and meeting scheduling tools</li>
          </ul>

          <h3 style={{ fontSize: '22px', fontWeight: '600', marginTop: '24px', marginBottom: '12px', color: colors.white }}>
            2.2 Information Collected Automatically
          </h3>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Device Information: IP address, browser type, device identifiers</li>
            <li style={{ marginBottom: '8px' }}>Usage Information: pages visited, time spent on pages, referring URLs, interactions with site elements</li>
            <li style={{ marginBottom: '8px' }}>Tracking Information: cookies, pixel tags, and analytics data</li>
          </ul>

          <h3 style={{ fontSize: '22px', fontWeight: '600', marginTop: '24px', marginBottom: '12px', color: colors.white }}>
            2.3 Account Information
          </h3>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Login credentials created during onboarding</li>
            <li style={{ marginBottom: '8px' }}>Portal usage history</li>
            <li style={{ marginBottom: '8px' }}>Application status, metadata, and historical submissions</li>
          </ul>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            3. How We Collect Information
          </h2>
          <p style={{ marginBottom: '24px' }}>
            We collect Personal Information from several sources: Direct submission through Fillout forms, application uploads, and communication tools - Automated technologies, including cookies, pixels, scripts, and session analysis tools - User portal interactions during onboarding or due-diligence workflows - Third-party services (e.g., identity verification partners or data rooms) when authorized by you
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            4. How We Use Information
          </h2>
          <p style={{ marginBottom: '24px' }}>
            We process Personal Information for the following purposes:
          </p>

          <h3 style={{ fontSize: '22px', fontWeight: '600', marginTop: '24px', marginBottom: '12px', color: colors.white }}>
            4.1 Operational & Application-Related Purposes
          </h3>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Verifying mining projects, bullion provider submissions, or commercial partner applications</li>
            <li style={{ marginBottom: '8px' }}>Conducting due diligence and evaluating eligibility</li>
            <li style={{ marginBottom: '8px' }}>Facilitating onboarding into the Arizore Reserve</li>
            <li style={{ marginBottom: '8px' }}>Providing and managing portal access</li>
          </ul>

          <h3 style={{ fontSize: '22px', fontWeight: '600', marginTop: '24px', marginBottom: '12px', color: colors.white }}>
            4.2 Communications
          </h3>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Responding to inquiries</li>
            <li style={{ marginBottom: '8px' }}>Providing application updates or requests for information</li>
            <li style={{ marginBottom: '8px' }}>Sending administrative notifications related to the onboarding process</li>
          </ul>

          <h3 style={{ fontSize: '22px', fontWeight: '600', marginTop: '24px', marginBottom: '12px', color: colors.white }}>
            4.3 Compliance & Legal Obligations
          </h3>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Meeting regulatory reporting requirements</li>
            <li style={{ marginBottom: '8px' }}>Conducting risk assessment and fraud prevention</li>
            <li style={{ marginBottom: '8px' }}>Cooperating with auditors, legal authorities, and compliance partners</li>
          </ul>

          <h3 style={{ fontSize: '22px', fontWeight: '600', marginTop: '24px', marginBottom: '12px', color: colors.white }}>
            4.4 Website & Platform Improvements
          </h3>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Enhancing website functionality and security</li>
            <li style={{ marginBottom: '8px' }}>Monitoring usage trends and performance analytics</li>
            <li style={{ marginBottom: '8px' }}>Developing new features, services, or integrations</li>
          </ul>

          <p style={{ marginBottom: '24px' }}>
            No automated decision-making is used for approvals; all application decisions involve human review.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            5. Legal Basis for Processing (Where Applicable)
          </h2>
          <p style={{ marginBottom: '12px' }}>
            Where data protection laws require a legal basis for processing (e.g., GDPR, UK GDPR), Arizore relies on the following:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Contractual Necessity: to provide onboarding, portal access, and due-diligence services</li>
            <li style={{ marginBottom: '8px' }}>Legitimate Interests: to operate, secure, and improve our systems; to verify applications; to prevent fraud or misuse</li>
            <li style={{ marginBottom: '8px' }}>Consent: for optional features such as marketing communications or non-essential cookies</li>
            <li style={{ marginBottom: '8px' }}>Legal Obligations: to comply with regulatory, tax, or corporate requirements</li>
          </ul>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            6. How We Share Information
          </h2>
          <p style={{ marginBottom: '24px' }}>
            Arizore may share Personal Information with the following categories of recipients:
          </p>

          <h3 style={{ fontSize: '22px', fontWeight: '600', marginTop: '24px', marginBottom: '12px', color: colors.white }}>
            6.1 Verified Partners
          </h3>
          <p style={{ marginBottom: '12px' }}>
            We share relevant information only with verified partners directly involved in supporting the Reserve, including:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Vaulting and bullion partners</li>
            <li style={{ marginBottom: '8px' }}>Geological and technical partners</li>
            <li style={{ marginBottom: '8px' }}>Liquidity partners</li>
            <li style={{ marginBottom: '8px' }}>Commercial partners providing operational, technological, or ecosystem services</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            These partners undergo review to ensure they meet Arizore's required standards.
          </p>

          <h3 style={{ fontSize: '22px', fontWeight: '600', marginTop: '24px', marginBottom: '12px', color: colors.white }}>
            6.2 Service Providers
          </h3>
          <p style={{ marginBottom: '12px' }}>
            Arizore uses third-party processors to support operations, including:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Fillout (form intake)</li>
            <li style={{ marginBottom: '8px' }}>Airtable (storage and internal workflows)</li>
            <li style={{ marginBottom: '8px' }}>Hosting, infrastructure, and platform providers</li>
            <li style={{ marginBottom: '8px' }}>Authentication and identity tools</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            These service providers only access information as necessary to perform services on our behalf.
          </p>

          <h3 style={{ fontSize: '22px', fontWeight: '600', marginTop: '24px', marginBottom: '12px', color: colors.white }}>
            6.3 Legal, Regulatory, and Compliance Recipients
          </h3>
          <p style={{ marginBottom: '12px' }}>
            We may disclose information to:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Government authorities</li>
            <li style={{ marginBottom: '8px' }}>Legal advisors</li>
            <li style={{ marginBottom: '8px' }}>Auditors or compliance professionals</li>
            <li style={{ marginBottom: '8px' }}>Courts or law enforcement agencies</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            Such disclosures occur only when required by law or necessary to protect our rights.
          </p>
          <p style={{ marginBottom: '24px' }}>
            We do not sell Personal Information.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            7. International Data Transfers
          </h2>
          <p style={{ marginBottom: '12px' }}>
            Given the global nature of our operations and partnerships, Personal Information may be transferred to jurisdictions outside your country of residence. Where legally required, we implement appropriate safeguards, including:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Contractual clauses</li>
            <li style={{ marginBottom: '8px' }}>Secure transfer mechanisms</li>
            <li style={{ marginBottom: '8px' }}>Restricted access practices</li>
          </ul>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            8. Data Retention
          </h2>
          <p style={{ marginBottom: '12px' }}>
            Arizore retains Personal Information for the minimum period necessary to fulfill the purposes outlined in this Policy, including:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Application processing and due-diligence activities</li>
            <li style={{ marginBottom: '8px' }}>Maintaining active user accounts</li>
            <li style={{ marginBottom: '8px' }}>Meeting legal or regulatory obligations</li>
            <li style={{ marginBottom: '8px' }}>Resolving disputes and maintaining system integrity</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            Retention durations vary based on the data type, applicable law, and operational requirements.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            9. Cookies and Tracking Technologies
          </h2>
          <p style={{ marginBottom: '12px' }}>
            Arizore uses cookies and similar tracking tools to:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Provide essential website features</li>
            <li style={{ marginBottom: '8px' }}>Analyze website usage and performance</li>
            <li style={{ marginBottom: '8px' }}>Improve user experience</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            Users can manage or disable cookies via browser settings; however, this may affect site functionality.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            10. Your Privacy Rights
          </h2>
          <p style={{ marginBottom: '12px' }}>
            Depending on your jurisdiction, you may have rights to:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Access Personal Information we hold about you</li>
            <li style={{ marginBottom: '8px' }}>Request correction of inaccurate information</li>
            <li style={{ marginBottom: '8px' }}>Request deletion of Personal Information</li>
            <li style={{ marginBottom: '8px' }}>Restrict or object to processing</li>
            <li style={{ marginBottom: '8px' }}>Request data portability</li>
            <li style={{ marginBottom: '8px' }}>Withdraw consent (where processing is based on consent)</li>
            <li style={{ marginBottom: '8px' }}>Lodge a complaint with a supervisory authority</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            To exercise rights, contact admin@azreserves.com.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            11. Security Measures
          </h2>
          <p style={{ marginBottom: '12px' }}>
            We implement reasonable technical and organizational measures, including:
          </p>
          <ul style={{ marginBottom: '24px', paddingLeft: '24px' }}>
            <li style={{ marginBottom: '8px' }}>Access controls and authentication safeguards</li>
            <li style={{ marginBottom: '8px' }}>Encryption and secure data storage practices</li>
            <li style={{ marginBottom: '8px' }}>Separation of duties within operational workflows</li>
            <li style={{ marginBottom: '8px' }}>Monitoring for unauthorized access or anomalies</li>
          </ul>
          <p style={{ marginBottom: '24px' }}>
            Despite these measures, no system is completely secure. Users acknowledge that transmitting data is done at their own risk.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            12. Third-Party Links & External Sites
          </h2>
          <p style={{ marginBottom: '24px' }}>
            Our website may reference third-party websites. Arizore does not control and is not responsible for their privacy practices or content. Users should review the privacy policies of any external sites before interacting.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            13. Children's Privacy
          </h2>
          <p style={{ marginBottom: '24px' }}>
            Arizore does not knowingly collect information from individuals under the age of 18. If we become aware that such information has been collected, we will take steps to delete it promptly.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            14. Changes to This Policy
          </h2>
          <p style={{ marginBottom: '24px' }}>
            Arizore may update this Privacy Policy periodically. Material changes will be posted on this page with an updated "Last Updated" date. Continued use of our website constitutes acceptance of revised terms.
          </p>

          <h2 style={{ fontSize: '28px', fontWeight: 'bold', marginTop: '32px', marginBottom: '16px', color: colors.white }}>
            15. Contact Us
          </h2>
          <p style={{ marginBottom: '12px' }}>
            For questions or concerns relating to this Privacy Policy, or to exercise your rights, contact:
          </p>
          <p style={{ marginBottom: '12px' }}>
            Email: admin@azreserves.com
          </p>
          <p style={{ marginBottom: '24px' }}>
            Address: The Provident House, Central Government Road, Charlestown, Nevis, W.I.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
